import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Divider,
  MenuItem,
  Modal, 
  Switch, 
  TextField, 
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../../components/buttons/Button';

import { 
  CreatePaymentCountryCurrencyXrefBIZcoreCC, 
  UpdatePaymentCountryCurrencyXrefBIZcoreCC
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import global from "../../../../../../global.scss";

const Data = {
  Country: "",
  // `Currency` must be seeded here too. Without it formData.Currency starts as
  // undefined, MUI mounts that Select as UNCONTROLLED, and the value set later
  // in edit mode never drives the displayed selection — which is why the
  // country populated on edit but the currency did not.
  Currency: "",
  IsPurposeCodeReq: false
};

const PaymentCountryCurrencyXrefModal = ({
  open, 
  setOpen, 
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.cc"]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(1);

  const [countriesList, setCountriesList] = useState([]);
  const [currenciesList, setCurrenciesList] = useState([]);

  const handleClose = () => {

    setOpen(false);
    setFormData(Data);
    setMode(1);
  }

  useEffect(() => {
    try {
      if(open){
        if(update.defaultMode === 1){
          // mode = 1 - create payment country currency xref
          setMode(1);
        }else if(update.defaultMode === 2){
          // mode = 2 - edit payment country currency xref
          setMode(2);
          setFormData({
            ...formData,
            Country: update.ref_payment_country_id?.id || "",
            Currency: update.ref_payment_currency_id?.id || "",

            IsPurposeCodeReq: update.is_require_purpose_code,
          });
        }else{
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    try {
      formatCountriesList(state.paymentCountriesList.records || []);
      formatCurrenciesList(state.paymentCurrenciesList.records || []);
    } catch (err) {}
  }, [state]);

  // Sorted by name — the raw API order is insertion order, which makes a
  // 250-entry dropdown unusable.
  const formatCountriesList = (list) => {
    setCountriesList([...list].sort((a, b) =>
      (a.ref_country_id?.name || "").localeCompare(b.ref_country_id?.name || "")));
  };  

  const formatCurrenciesList = (list) => {
    setCurrenciesList([...list].sort((a, b) =>
      (a.ref_currency_id?.name || "").localeCompare(b.ref_currency_id?.name || "")));
  }; 

  // --------------- handle change ----------------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if(mode === 1){
      // create country
      let obj = {
        ref_payment_country_id: formData.Country || "",
        ref_payment_currency_id: formData.Currency || "",
        is_require_purpose_code: formData.IsPurposeCodeReq
      };
      dispatch(CreatePaymentCountryCurrencyXrefBIZcoreCC(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        })
      });
    }else if(mode === 2){
      // update country
      let obj = {
        ref_payment_country_id: formData.Country || "",
        ref_payment_currency_id: formData.Currency || "",
        is_require_purpose_code: formData.IsPurposeCodeReq
      };
      dispatch(UpdatePaymentCountryCurrencyXrefBIZcoreCC(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        })
      });
    }else{
      toast.error("Invalid modal");
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"}
            sx={{ padding: "15px"}}>
            <Typography variant='h6' 
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                select
                label="Payment Countries"
                fullWidth
                name="Country"
                value={formData.Country}
                onChange={handleChange}
                inputProps={{ maxLength: 64 }}
                >
                {
                  countriesList.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.id}>
                      {item.ref_country_id?.name}
                    </MenuItem>
                  ))
                }
              </TextField>
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                select
                label="Payment Currencies"
                fullWidth
                name="Currency"
                value={formData.Currency}
                onChange={handleChange}
                inputProps={{ maxLength: 64 }}
                >
                {
                  currenciesList.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.id}>
                      {item.ref_currency_id?.name} - {item.ref_currency_id?.code}
                    </MenuItem>
                  ))
                }
              </TextField>
            </Box>
            <Box 
              className="flexCenter_Row"
              gap={"20px"}
              mb={"15px"}>
              <Typography>  
                Is purpose code required
              </Typography> 
              <Switch 
                checked={formData.IsPurposeCodeReq}
                onChange={(e) => {
                  setFormData({ ...formData, IsPurposeCodeReq: e.target.checked });
                }}
                />
            </Box>
          </Box>
          <Divider
            sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px"}}>
            <Button variant='text'
              onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton 
              variant='contained'
              loading={loading}
              disabled={!(formData.Country !== ""  && formData.Currency !== "")}
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default PaymentCountryCurrencyXrefModal;